import { generateSort, generateFilter } from "./utils";
import queryString from "query-string";
import type { DataProvider } from "@refinedev/core";

type MethodTypes = "get" | "delete" | "head" | "options";
type MethodTypesWithBody = "post" | "put" | "patch";
const orderOps: { [key: string]: string } = {
  'asc': "ASCENDING",
  'desc': "DESCENDING"
}

// 'https://people.googleapis.com/v1/people/me?requestMask.includeField=person.names',
// https://content.googleapis.com/calendar/v3/calendars/primary/events?maxResults=25&orderBy=startTime&showDeleted=false&singleEvents=true&timeMin=2024-06-01T21%3A45%3A16.166Z&key=AIzaSyDrCi0JocspbRDKoxsjPfotKeAksT56f50

const dataProvider = (
  gapi: any,
  apiUrl: string = 'https://content.googleapis.com',
  version: string = 'v3',
): DataProvider => { //Omit<
  //Required<DataProvider>,
  //"createMany" | "updateMany" | "deleteMany"
  //> 
  // if(!gapi) {
  //   return {}
  // }
  if (gapi) {
    gAPI = gapi
  } else {
    throw new Error("Google API not ready")
  }

  const checkAuth = (authEnabled: boolean) => {
    return authEnabled && !!gAPI.client?.getToken()
  }

  return {
    getList: async ({ resource, pagination, filters, sorters, meta }) => {
      if (!checkAuth(meta?.auth ?? true)) {
        throw new Error("auth is not enabled")
      }

      const [service, ...rest] = resource.split('/')
      if (meta?.version) {
        version = meta.version
      }
      let url = meta?.url ? `${meta.url}/${service}${rest.length ? '/' + rest.join('/') : ''}` :
        `${apiUrl}/${service}/${version}${rest.length ? '/' + rest.join('/') : ''}`;
      let fields

      if (meta?.params)
        Object.keys(meta.params).forEach(v => url = url.replace(':' + v, encodeURIComponent(meta.params[v])))

      if (meta?.queryParams)
        url = `${url}?${queryString.stringify(meta?.queryParams)}`

      if (meta?.fields) {
        fields = { fields: `items(${meta.fields.join(',')})` } //TODO: support nested cases (https://developers.google.com/blogger/docs/3.0/performance)
      }
      const { pageSize = 10, mode = "server" } = pagination ?? {};

      const { headers: headersFromMeta, method, body } = meta ?? {};
      const requestMethod = (method as MethodTypes) ?? "get";

      const queryFilters = generateFilter(filters);

      const query: {
        maxResults?: number;
        pageToken?: string;
        orderBy?: string;
        sortOption?: string;
      } = {};

      if (mode === "server") {
        query.maxResults = pageSize
        if (meta?.cursor?.next) {
          query.pageToken = meta.cursor.next
        }
      }

      const generatedSort = generateSort(sorters);
      if (generatedSort) {
        const { _sort, _order } = generatedSort;
        query.orderBy = _sort.join(",");
        const sort = _order.filter(o => o != 'desc')
        if (sort.length > 0)
          query.sortOption = sort.map(o => orderOps[o]).join(',')
      }

      const combinedQuery = { ...query, ...queryFilters, ...fields };
      const urlWithQuery = Object.keys(combinedQuery).length
        ? `${url}?${queryString.stringify(combinedQuery, { arrayFormat: 'comma' })}`
        : url;

      const { result: data } = await gapi.client.request({
        path: urlWithQuery,
        method: requestMethod,
        headers: headersFromMeta,
        ...(body && { body })
      })

      const { items, nextPageToken, ...r } = data
      const total = items?.length ?? 0

      return {
        data: items,
        total: total || data.length,
        cursor: {
          next: nextPageToken,
          // prev: data.cursor.prev,
        },
        ...r,
      };
    },
    // getMany: async ({ resource, ids, meta }) => {
    //   const { headers, method } = meta ?? {};
    //   const requestMethod = (method as MethodTypes) ?? "get";

    //   const { data } = await httpClient[requestMethod](
    //     `${apiUrl}/${resource}?${stringify({ id: ids })}`,
    //     { headers },
    //   );

    //   return {
    //     data,
    //   };
    // },

    create: async ({ resource, variables, meta }) => {
      if (!checkAuth(meta?.auth ?? true)) {
        throw new Error("auth is not enabled")
      }

      const [service, ...rest] = resource.split('/')
      if (meta?.version) {
        version = meta.version
      }
      let url = meta?.url ? `${meta.url}/${service}${rest.length ? '/' + rest.join('/') : ''}` :
        `${apiUrl}/${service}/${version}${rest.length ? '/' + rest.join('/') : ''}`;
      if (meta?.params)
        Object.keys(meta.params).forEach(v => url = url.replace(':' + v, encodeURIComponent(meta.params[v])))

      if (meta?.queryParams)
        url = `${url}?${queryString.stringify(meta?.queryParams, { arrayFormat: 'comma' })}`

      const { headers, method } = meta ?? {};
      const requestMethod = (method as MethodTypesWithBody) ?? "post";

      const { result: data } = await gapi.client.request({
        path: url,
        method: requestMethod,
        headers: headers,
        body: variables
      })

      return {
        data,
      };
    },

    update: async ({ resource, id, variables, meta }) => {
      if (!checkAuth(meta?.auth ?? true)) {
        throw new Error("auth is not enabled")
      }

      // const url = `${apiUrl}/${resource}/${id}`;
      const [service, ...rest] = resource.split('/')
      if (meta?.version) {
        version = meta.version
      }
      let url = meta?.url ? `${meta.url}/${service}${rest.length ? '/' + rest.join('/') : ''}/${id}` :
        `${apiUrl}/${service}/${version}${rest.length ? '/' + rest.join('/') : ''}/${id}`;

      if (meta?.params)
        Object.keys(meta.params).forEach(v => url = url.replace(':' + v, encodeURIComponent(meta.params[v])))

      if (meta?.queryParams)
        url = `${url}?${queryString.stringify(meta?.queryParams, { arrayFormat: 'comma' })}`

      const { headers, method } = meta ?? {};
      const requestMethod = (method as MethodTypesWithBody) ?? "put";

      if (requestMethod == 'patch') {
        const { result: data } = await gapi.client.request({
          path: url,
          method: requestMethod,
          headers: headers,
          body: variables
        })

        return {
          data,
        };
      } else {
        const { result: dataGet } = await gapi.client.request({
          path: url,
          method: 'get',
          headers: headers,
          // body: variables
        })

        const { result: data } = await gapi.client.request({
          path: url,
          method: requestMethod,
          headers: headers,
          body: { ...dataGet, ...variables }
        })

        return {
          data,
        };
      }
    },

    getOne: async ({ resource, id, meta }) => {
      if (!checkAuth(meta?.auth ?? true)) {
        throw new Error("auth is not enabled")
      }

      const [service, ...rest] = resource.split('/')
      if (meta?.version) {
        version = meta.version
      }

      let url = meta?.url ? `${meta.url}/${service}${rest.length ? '/' + rest.join('/') : ''}/${id}` :
        `${apiUrl}/${service}/${version}${rest.length ? '/' + rest.join('/') : ''}/${id}`;

      if (meta?.params)
        Object.keys(meta.params).forEach(v => url = url.replace(':' + v, encodeURIComponent(meta.params[v])))

      if (meta?.queryParams)
        url = `${url}?${queryString.stringify(meta?.queryParams, { arrayFormat: 'comma' })}`

      const { headers, method } = meta ?? {};
      const requestMethod = (method as MethodTypes) ?? "get";

      const { result: data } = await gapi.client.request({
        path: url,
        method: requestMethod,
        headers: headers,
      })

      return {
        data,
      };
    },

    deleteOne: async ({ resource, id, variables, meta }) => {
      if (!checkAuth(meta?.auth ?? true)) {
        throw new Error("auth is not enabled")
      }

      const [service, ...rest] = resource.split('/')
      if (meta?.version) {
        version = meta.version
      }
      let url = meta?.url ? `${meta.url}/${service}${rest.length ? '/' + rest.join('/') : ''}/${id}` :
        `${apiUrl}/${service}/${version}${rest.length ? '/' + rest.join('/') : ''}/${id}`;

      if (meta?.params)
        Object.keys(meta.params).forEach(v => url = url.replace(':' + v, encodeURIComponent(meta.params[v])))

      if (meta?.queryParams)
        url = `${url}?${queryString.stringify(meta?.queryParams, { arrayFormat: 'comma' })}`

      const { headers, method } = meta ?? {};
      const requestMethod = (method as MethodTypesWithBody) ?? "delete";

      const { result: data } = await gapi.client.request({
        path: url,
        method: requestMethod,
        headers: headers,
        body: variables
      })

      return {
        data,
      };
    },

    getApiUrl: () => {
      return apiUrl;
    },

    custom: async ({
      url,
      method,
      filters,
      sorters,
      payload,
      query,
      headers,
      meta,
    }) => {
      if (!checkAuth(meta?.auth ?? true)) {
        throw new Error("auth is not enabled")
      }

      let requestUrl = `${url}?`;

      if (sorters) {
        const generatedSort = generateSort(sorters);
        if (generatedSort) {
          const { _sort, _order } = generatedSort;
          const sortQuery = {
            orderBy: _sort.join(","),
            // _order: _order.join(","),
          };
          requestUrl = `${requestUrl}&${queryString.stringify(sortQuery, { arrayFormat: 'comma' })}`;
        }
      }

      if (filters) {
        const filterQuery = generateFilter(filters);
        requestUrl = `${requestUrl}&${queryString.stringify(filterQuery, { arrayFormat: 'comma' })}`;
      }

      if (query) {
        requestUrl = `${requestUrl}&${queryString.stringify(query, { arrayFormat: 'comma' })}`;
      }

      let gapiResponse;
      switch (method) {
        case "put":
        case "post":
        case "patch":
          gapiResponse = await gapi.client.request({
            path: requestUrl,
            method: method,
            headers: headers,
            body: payload
          })
          break;
        case "delete":
          gapiResponse = await gapi.client.request({
            path: requestUrl,
            method: method,
            headers: headers,
            body: payload
          })
          break;
        default:
          gapiResponse = await gapi.client.request({
            path: requestUrl,
            method: method,
            headers: headers,
          })
          break;
      }

      const { result: data } = gapiResponse;

      return Promise.resolve({ data });
    },
  }
}

export default dataProvider


export let gAPI = window.gapi